import * as amplitude from '@amplitude/unified';
import Service, { service } from '@ember/service';
import { isTesting } from '@embroider/macros';

import config from 'later/config/environment';
import { ErrorSeverity } from 'later/services/errors';

import type ErrorsService from 'later/services/errors';

export interface AmplitudeUserTraits {
  accountId?: string;
  email?: string;
  name?: string;
  plan?: string;
}

export default class AmplitudeService extends Service {
  @service declare errors: ErrorsService;

  isInitialized = false;

  get apiKey(): string | undefined {
    return config.APP.amplitudeApiKey;
  }

  setup(): void {
    if (this.isInitialized || isTesting() || !this.apiKey) {
      return;
    }

    try {
      amplitude
        .initAll(this.apiKey, {
          analytics: {
            autocapture: {
              sessions: true,
              pageViews: true,
              formInteractions: true,
              elementInteractions: true
            }
          },
          // Sample rate of 1 (100%) so that whichever user later opens a Maven conversation
          // has replay data available for Autoplay to correlate -- partial sampling would mean
          // most support conversations have no replay to draw on.
          sessionReplay: {
            sampleRate: 1
          }
        })
        .catch((error: Error) => this.errors.log(error, {}, ErrorSeverity.Info));
      this.isInitialized = true;
    } catch (error) {
      this.errors.log(error as Error, {}, ErrorSeverity.Info);
    }
  }

  identify(userId: string, traits: AmplitudeUserTraits = {}): void {
    if (!this.isInitialized) {
      this.setup();
    }

    if (!this.isInitialized) {
      return;
    }

    try {
      amplitude.setUserId(userId);

      const identifyEvent = new amplitude.Identify();
      let hasTraits = false;

      if (traits.accountId) {
        identifyEvent.set('account_id', traits.accountId);
        hasTraits = true;
      }
      if (traits.email) {
        identifyEvent.set('email', traits.email);
        hasTraits = true;
      }
      if (traits.name) {
        identifyEvent.set('name', traits.name);
        hasTraits = true;
      }
      if (traits.plan) {
        identifyEvent.set('plan', traits.plan);
        hasTraits = true;
      }

      if (hasTraits) {
        amplitude.identify(identifyEvent);
      }
    } catch (error) {
      this.errors.log(error as Error, {}, ErrorSeverity.Info);
    }
  }

  reset(): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      amplitude.reset();
    } catch (error) {
      this.errors.log(error as Error, {}, ErrorSeverity.Info);
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    amplitude: AmplitudeService;
  }
}
